import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit} from '@angular/core';
import {IFormSection, ISummarySection} from '@ispm/therapies/interfaces/view-configuration.interface';
import {WorkflowConfigService} from '@ispm/workflow/services/workflow-config.service';
import {Resources} from '@ispm/workflow/constants/resources.enum';
import {Observable} from 'rxjs/Observable';
import {catchError, filter, flatMap, map, pluck, switchMap, take, takeUntil, tap, withLatestFrom} from 'rxjs/operators';
import {combineLatest} from 'rxjs/observable/combineLatest';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Subject} from 'rxjs/Subject';
import {IFormConfiguration} from '@ispm/therapies/interfaces/section-configuration.interface';
import {TherapiesActionCreatorService} from '@ispm/therapies/services/therapies-action-creator.service';
import * as Immutable from 'immutable';
import {currentDiagnosticOrderSelector} from '@ispm/test/store/diagnostic-order.selector';
import {modalResponse} from '@ispm/layout/constants/modal-responses.enum';
import {DateHandler} from '@ispm/shared/utils/DateHandler';
import {IDiagnosticOrder} from '@ispm/test/interfaces/diagnostic-order.interface';
import {IModalConfiguration} from '@ispm/shared/interfaces/action.interface';
import {TherapiesService} from '@ispm/therapies/services/therapies.service';
import {IgenomicsState} from '@ispm/core/interfaces/genomics-state.interface';
import {CoreActionCreatorService} from '@ispm/core/services/core-action-creator.service';
import {RouterManagerService} from '@ispm/core/services/router-manager.service';
import {Store} from '@ngrx/store';
import {DiagnosticOrderActionsService} from '@ispm/test/services/diagnostic-order-actions.service';
import {FormHandlerService} from '@ispm/dynamic-form/services/form-handler.service';
import {ButtonAction} from '@ispm/shared/classes/button-action.class';
import {findKey, get, has, isEmpty, set} from 'lodash';
import {DynamicFormModalResponse} from '@ispm/dynamic-form/classes/dynamic-form-modal-response.class';
import {ConditionalItem} from '@ispm/shared/classes/conditional-item.class';
import {of} from 'rxjs/observable/of';
import {NOTIFICATION_MESSAGE} from "@ispm-client/modules/aggregator/services/consts";
import {NavigationConfigService} from "@ispm/navigation/services/navigationConfig.service";
import {IEService} from "@ispm/shared/services/IE.service";
import {TestService} from '@ispm/dashboard/test/services/test.service';
import {NotificationMessageService} from '@ispm/core/components/customise-notification/notifications-message.service';
import {activeTabObject} from "@ispm/test/constants/Tabs.constants";
import { EMPTY } from 'rxjs';
import { PollJobService } from '@ispm/test/services/poll-job.service';

@Component({
  selector: 'ispm-treatment-summary',
  templateUrl: './treatment-summary.component.html',
  styleUrls: ['./treatment-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TreatmentSummaryComponent implements OnInit, OnDestroy {

  @Input()
  isPerformer: boolean;
  private userReview$: Observable<Immutable.List<any>>;

  @Input()
  public changeOpinionOfMIIn:boolean;

  @Input()
  set summarySectionConfiguration(summarySectionConfiguration: ISummarySection) {
    this.setConfiguration(summarySectionConfiguration);
    this.setActions();
    this._diagnosticOrderAC.getDiscipline().take(1)
      .subscribe(discipline => this.discipline = discipline);
  }

  get summarySectionConfiguration() {
    return this._summarySectionConfiguration;
  }

  private _editStatuses: any;
  @Input()
  set editStatuses(editStatuses: any) {
    this._editStatuses = editStatuses;
    this.setActions();
  }

  get editStatuses() {
    return this._editStatuses;
  }

  public activeTreatment$: Observable<any> = of(null);
  public actionsListenerDestroyer$: Subject<null> = new Subject();
  public formConfigs: Array<IFormConfiguration>;
  public workflowId$: Observable<string>;
  public revisionId$: Observable<string>;
  public secondaryButtonActions: Array<ButtonAction>;
  public primaryButtonActions: Array<ButtonAction>;
  public inprogress: boolean = false;
  private _summarySectionConfiguration: ISummarySection;
  private activeSelection: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private _actions: { [prop: string]: (...args) => void } = {
    reset: this.initReview.bind(this),
    signoff: this.signoff.bind(this),
    adjust: this.adjust.bind(this),
    submit: this.submit.bind(this),
    submit_with_modal: this.submitTreatment.bind(this),
    submit_fixed_intent: this.submitFixedIntent.bind(this)
  };
  private destroyer$: Subject<any> = new Subject<any>();
  private discipline: any;
  private currApplicationId: string;
  private reportForDiscipline: any;
  constructor(
    private workflowConfig: WorkflowConfigService,
    private therapiesAC: TherapiesActionCreatorService,
    private therapiesService: TherapiesService,
    private store: Store<IgenomicsState>,
    private testService: TestService,
    private coreAC: CoreActionCreatorService,
    private _workflowService: WorkflowConfigService,
    private _diagnosticOrderAC: DiagnosticOrderActionsService,
    private _formHandler: FormHandlerService,
    private _zone: NgZone,
    private _changeDetector: ChangeDetectorRef,
    private notificationMessageService: NotificationMessageService,
    private notificationMessage: NOTIFICATION_MESSAGE,
    private ieService: IEService,
    private navigationConfigService: NavigationConfigService,
    private routerManagerService: RouterManagerService,
    private pollJobService: PollJobService,
  ) {
    const resourceMap$ = this.workflowConfig.getResourcesMap();
    this.revisionId$ = resourceMap$.pipe(pluck(Resources.RequestedRevision));
    this.workflowId$ = resourceMap$.pipe(pluck(Resources.WorkflowRequest));
  }

  ngOnInit() {
    this.currApplicationId = this.navigationConfigService.getCurrentApplicationId();
    this.store.select('users', 'organizationConfiguration')
      .pipe(takeUntil(this.destroyer$.asObservable()))
      .subscribe(data => {
        this.reportForDiscipline = get(data, 'reportForDiscipline');
      });
  }

  setConfiguration(configuration) {
    this._summarySectionConfiguration = configuration;
    switch (this.summarySectionConfiguration.type) {
      case 'form':
        this.formConfigs = (<IFormSection> this.summarySectionConfiguration).payload.configs || [];
        this.revisionId$.pipe(
          take(1),
          tap(this.setUserReviewSubscribers.bind(this))
        ).subscribe();
        break;
      case 'priority':
        break;
      case 'reflex':
        break;
    }
  }
  trackByFn(index, item) {
    return item.formId ? `${item.formId}_${index}` : index;
  }

  setUserReviewSubscribers() {
    this.userReview$ = this.therapiesAC.getState('userReview')
      .pipe(
        filter(userReview => !!userReview),
        pluck('priority')
      );
    this.activeTreatment$ = combineLatest(this.userReview$, this.activeSelection)
      .pipe(
        map(([priority, index]) => {
          return (<Immutable.List<any>> priority).toArray()[index];
        })
      );
  }

  initReview() {
    this.therapiesAC.initUserReviewState(null, true).then();
  }

  signoff() {
    return this.store.select(currentDiagnosticOrderSelector).pipe(
      take(1),
      withLatestFrom(this.revisionId$, this.workflowId$),
      tap(() => this.coreAC.setLoader(true)),
      switchMap(([currentTest, revisionId, workflowId]) =>
        this.therapiesService.signOff(currentTest, 'final review', '', revisionId, workflowId, '').pipe(
          tap(success => {
            if (!success) {
              throw new Error('Can\'t proceed with Sign off');
            }
          }),
          map(() => ({ revisionId, workflowId})),
        )
      ),
      tap(({ revisionId, workflowId}) => {

        this.workflowConfig.getCurrentActiveWorkflow().toPromise()
          .then(activeWorkFlow => this.pollJobService.waitForWorkflowCondition(workflowId, revisionId,
              workflow => workflow.activeJobName !== activeWorkFlow.activeJobName, 2000, 20)
          )
          .then(workflow => {
            if (workflow.activeJobName.toLowerCase().includes('fail')) {
              throw new Error('Can\'t perform workflow step');
            }
            this.coreAC.setLoader(false);
            if (this.testService.testConfiguration.xHtmlReportTemplates) {
              this.testService.activeTab = activeTabObject.REPORT_PREVIEW;
            } else {
              this.testService.activeTab = activeTabObject.TEST_SUMMARY;
            }
            this.routerManagerService.reload();
          })
          .catch(error => {
            console.error(error);
            this.notificationMessageService.error('Can\'t complete sign off');
          })
          .finally(() => this.coreAC.setLoader(false));
      }),
      catchError(error => {
        console.error(error);
        this.notificationMessageService.error(error.message);
        this.coreAC.setLoader(false);
        return EMPTY;
      }),
    )
    .subscribe();
  }

  submit(payload) {
    return this.therapiesAC.sendPriority()
      .pipe(take(1))
      .subscribe(
        () => {
          this.initReview();
          this.coreAC.setLoader(false);
        },
        () => this.coreAC.setLoader(false)
      );
  }

  updateActiveTreatment(formValue: any) {
    combineLatest([this.userReview$, this.activeSelection])
      .pipe(
        take(1),
        map(([treatments, index]) => {
          const activeTreatment = treatments.toArray()[index];
          Object.assign(activeTreatment, formValue);
          return [index, activeTreatment];
        })
      ).subscribe(([index, treatment]) => {
        this.therapiesAC.updateUserReview(index, treatment);
    });
  }

  submitFixedIntent(payload){

    forkJoin([
      this._diagnosticOrderAC.getState<IDiagnosticOrder>('order').pipe(take(1)),
      this.userReview$.pipe(take(1))
    ]).pipe(
      flatMap(res => {
        const [diagnosticOrder, treatments] = res;
        const formData = { intent: payload.additionalData.intent,  status: payload.additionalData.status };
        // get client date/time;
        const dateTime = DateHandler.toUsStandardStrings(new Date());
        Object.assign(formData, { creationDate: dateTime.date, creationTime: dateTime.time }, payload.additionalData);
        const treatment = { treatments, formData };

        // move to next step
        return this._workflowService.performWorkflowStep(diagnosticOrder as IDiagnosticOrder, null, treatment);
      })
    ).subscribe(() => {
      this.routerManagerService.reload();
    });
  }

  private isNotEmptyProperty(txt: string): boolean {
    return !!txt && !isEmpty(txt.trim());
  }

  submitTreatment(payload) {

    this.userReview$.pipe(map((userReview) => userReview.asImmutable().toArray()), take(1))
      .subscribe(treatments => {
        const hasTreatments = treatments && treatments.length > 0;
        const isOffPathway = hasTreatments ? get(treatments[0], 'offPathway', false) : false;
        // GEN-179461: Off-Path selection must include reasoning
        const offPathwayHasData = hasTreatments ? (!isEmpty(get(treatments[0], 'reason'))) && (treatments[0].resourceType || has(treatments[0], 'comments')) : false;

        let isOtherReasonExtraDetailsValid: boolean = true;
        if (isOffPathway && (get(treatments[0], 'reason') === "Other Reason")) {
          isOtherReasonExtraDetailsValid = this.isNotEmptyProperty(treatments[0].reasonExtraDetails);
        }

        let isCommentValid: boolean = true;
        const arrTPSelection = ['Off-Pathway Drug or Drug Combination / Unlisted Clinical Trial', 'Off-Pathway Therapy or Therapy Combination / Unlisted Clinical Trial'];
        if (isOffPathway && hasTreatments &&  arrTPSelection.includes(treatments[0].treatmentPlansSelection)) {
          isCommentValid = !isEmpty(treatments[0].comments);
        }

        let offPathHasOption: boolean = true;
        if (isOffPathway && hasTreatments && treatments[0].hasOwnProperty('treatmentPlansSelection')){
          offPathHasOption = arrTPSelection.includes(treatments[0].treatmentPlansSelection);
        }

        if (hasTreatments && ((isOffPathway && offPathwayHasData && isOtherReasonExtraDetailsValid && isCommentValid && offPathHasOption) || !isOffPathway)) {
          let formId = payload.formId;
          if (has(payload, 'formIdByData')) {
            formId = this.getFormIdByData(get(payload, 'formIdByData', undefined), payload.formId);
          }
          const formData$ = this.popupDynamicFormModal(
            payload.title || '',
            formId,
            {},
            payload.approveBtn || 'Confirm',
            payload.declineBtn || 'Cancel'
          ).pipe(
            filter((data: any) => data.status === modalResponse.approve && data.payload.isValid),
            map(data => data.payload.formData)
          );

          forkJoin([
            formData$,
            this._diagnosticOrderAC.getState<IDiagnosticOrder>('order').pipe(take(1))
          ]).pipe(
            flatMap(res => {
              const [formData, diagnosticOrder] = res;

              // get client date/time
              const dateTime = DateHandler.toUsStandardStrings(new Date());
              Object.assign(formData, {
                creationDate: dateTime.date,
                creationTime: dateTime.time
              }, payload.additionalData);

              const treatment = {treatments, formData};
              const key = !!this.reportForDiscipline ? findKey(this.reportForDiscipline, (item) => {
                return !!item.code && item.code === diagnosticOrder.discipline.code; }) : undefined;
              diagnosticOrder.generateReport = !!key ? get(this.reportForDiscipline, key.concat('.show'), false) : false;
              // move to next step
              const patientId = this.routerManagerService.parameters.patientId;
              diagnosticOrder.subject =  diagnosticOrder.subject ||  patientId;
              return this._workflowService.performWorkflowStep(diagnosticOrder as IDiagnosticOrder, null, treatment);
            }),
            take(1),
          ).subscribe(() => {
            this.routerManagerService.reload();
          });
        }
        else {
          let msg: string = this.notificationMessage.PATHWAY_NO_TREATMENT_WAS_SELECTED; /*"No treatment was selected"*/
          if (isOffPathway) {
            if (!this.isNotEmptyProperty(treatments[0].reason)) {
              msg = this.notificationMessage.PATHWAY_OFF_PATH_NO_REASONING; /*"No reasoning was selected for Off-Path"*/
            } else if (!offPathHasOption) {
              msg = this.notificationMessage.PATHWAY_NO_OPTION_WAS_SELECTED; // "No treatment option was selected";
            } else if (!isOtherReasonExtraDetailsValid) {
              msg = this.notificationMessage.PATHWAY_NO_OTHER_REASON_EXTRA_DETAILS;
            } else if (!isCommentValid) {
              msg = this.notificationMessage.PATHWAY_NO_COMMENT_WAS_SELECTED;//"";
            }
          }
          this.notificationMessageService.error(msg);
        }
      });
  }

  adjust(payload) {
    this.activeTreatment$
      .pipe(
        take(1),
        flatMap((treatment: any) => this.popupDynamicFormModal('Adjust treatment', payload.formId, treatment, 'Submit', 'Cancel')),
        filter((data: any) => data.status === modalResponse.approve && data.payload.isValid),
        tap(data => {
          const modified = get(data, 'payload.formData.modified') || data.payload.updatedFields.length > 0;
          set(data, 'payload.formData.modified', modified);
          const updatedFields = get(data, 'payload.updatedFields', []);
          updatedFields.push.apply(updatedFields, data.payload.updatedFields);
          Object.defineProperty(data.payload.formData, 'updatedFields', {
            value: Array.from(new Set(updatedFields)),
            enumerable: false,
            configurable: true,
            writable: true
          });
        }),
        flatMap((data: DynamicFormModalResponse) => {
          return combineLatest(
            this.userReview$.pipe(map(userReview => userReview.toArray()), take(1)),
            this.activeSelection.pipe(take(1)),
            Observable.of(get(data, 'payload.formData', { }))
          );
        })
      ).subscribe(([userReviews, selectionIndex, formData]) => {
      userReviews[selectionIndex] = formData;
      this.therapiesAC.updateUserReview(selectionIndex, userReviews[selectionIndex]);
    });

  }

  runAction(action: (payload?: any) => void, config: IModalConfiguration, payload?: any) {
    this.therapiesService.runAction(() => action.apply(this, [payload]), config, this.destroyer$.asObservable());
  }

  popupDynamicFormModal(title: string, formId: string, formData: any, approveBtn?: string, declineBtn?: string) {
    return this._formHandler.popupDynamicFormModal(title, formId, formData, approveBtn, declineBtn, null, TherapiesService.ModalLoaderTunnelName);
  }

  setActions() {
    this.actionsListenerDestroyer$.next();
    const actions = get(this.summarySectionConfiguration, 'actions');
    this.secondaryButtonActions = [];
    this.primaryButtonActions = [];
    if (!actions) { return; }
    this.activeTreatment$
      .pipe(
        takeUntil(this.actionsListenerDestroyer$.asObservable()),
        // take(1),
      ).subscribe(resource => {
      this.secondaryButtonActions = [];
      this.primaryButtonActions = [];
      actions.forEach(action => {
        const validationResourceValues = { ...this.editStatuses, resource: resource};
        if (!this.validateConditions(action, validationResourceValues)) {
          return;
        }
        const actionItem = new ButtonAction(action.label, () => this.runAction(this._actions[action.command], action.modal, action.payload), action.modal, action.requiredPermission);
        switch (action.buttonType.toLowerCase()) {
          case 'primary':
            this.primaryButtonActions.push(actionItem);
            break;
          case 'secondary':
            this.secondaryButtonActions.push(actionItem);
            break;
        }
      });
    });
  }

  private validateConditions(action, validationResourceValues) {
    const conditions = action.conditions || [];
    const and = 'AND';
    const conditionsOperator = action.conditionsOperator || and;
    if (conditionsOperator === and) {
      return conditions.every(condition => ConditionalItem.builder(validationResourceValues, condition.value, condition.operator, condition.path).verify());
    } else {
      return conditions.some(condition => ConditionalItem.builder(validationResourceValues, condition.value, condition.operator, condition.path).verify());
    }
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();
    this.actionsListenerDestroyer$.next();
    this.actionsListenerDestroyer$.complete();
  }

  private getFormIdByData(formsData, defaultForm): string {
    let formId = defaultForm;
    if (!!formsData && Array.isArray(formsData) && formsData.length > 0) {
      formsData.forEach(descr => {
        if(get(this, descr.path, undefined) === descr.value){
           formId = descr.formId;
        }
       });
    }
    return formId;
  }


}
